/**
 * Call state machine.
 *
 *   INITIATED -> RINGING -> ACCEPTED -> CONNECTING -> CONNECTED -> ENDED
 *                   |          |
 *                   |          +-> FAILED
 *                   +-> MISSED (ring timeout, server-side)
 *                   +-> CANCELLED (guest hung up first)
 *                   +-> DECLINED (receptionist rejected)
 *
 * Only Cloud Functions ever write this field; see firestore.rules.
 */
export const CALL_STATUS = {
  INITIATED: 'INITIATED',
  RINGING: 'RINGING',
  ACCEPTED: 'ACCEPTED',
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  ENDED: 'ENDED',
  DECLINED: 'DECLINED',
  MISSED: 'MISSED',
  CANCELLED: 'CANCELLED',
  FAILED: 'FAILED',
} as const;

export type CallStatus = (typeof CALL_STATUS)[keyof typeof CALL_STATUS];

/** Statuses in which the call is still live and occupies a receptionist. */
export const ACTIVE_CALL_STATUSES: readonly CallStatus[] = [
  CALL_STATUS.INITIATED,
  CALL_STATUS.RINGING,
  CALL_STATUS.ACCEPTED,
  CALL_STATUS.CONNECTING,
  CALL_STATUS.CONNECTED,
];

/** Statuses from which no further transition is possible. */
export const TERMINAL_CALL_STATUSES: readonly CallStatus[] = [
  CALL_STATUS.ENDED,
  CALL_STATUS.DECLINED,
  CALL_STATUS.MISSED,
  CALL_STATUS.CANCELLED,
  CALL_STATUS.FAILED,
];

export function isActiveCallStatus(status: CallStatus): boolean {
  return ACTIVE_CALL_STATUSES.includes(status);
}

export function isTerminalCallStatus(status: CallStatus): boolean {
  return TERMINAL_CALL_STATUSES.includes(status);
}

/** Legal transitions, enforced server-side in the call transaction helpers. */
export const ALLOWED_CALL_TRANSITIONS: Readonly<
  Record<CallStatus, readonly CallStatus[]>
> = {
  INITIATED: [CALL_STATUS.RINGING, CALL_STATUS.FAILED, CALL_STATUS.CANCELLED],
  RINGING: [
    CALL_STATUS.ACCEPTED,
    CALL_STATUS.DECLINED,
    CALL_STATUS.MISSED,
    CALL_STATUS.CANCELLED,
    CALL_STATUS.FAILED,
  ],
  ACCEPTED: [CALL_STATUS.CONNECTING, CALL_STATUS.ENDED, CALL_STATUS.FAILED],
  CONNECTING: [CALL_STATUS.CONNECTED, CALL_STATUS.ENDED, CALL_STATUS.FAILED],
  CONNECTED: [CALL_STATUS.ENDED, CALL_STATUS.FAILED],
  ENDED: [],
  DECLINED: [],
  MISSED: [],
  CANCELLED: [],
  FAILED: [],
};

export function canTransition(from: CallStatus, to: CallStatus): boolean {
  return (ALLOWED_CALL_TRANSITIONS[from] ?? []).includes(to);
}

export const DEPARTMENT = {
  RECEPTION: 'RECEPTION',
  HOUSEKEEPING: 'HOUSEKEEPING',
  ROOM_SERVICE: 'ROOM_SERVICE',
} as const;

export type Department = (typeof DEPARTMENT)[keyof typeof DEPARTMENT];

/**
 * `hotels/{hotelId}/calls/{callId}`
 *
 * Deliberately contains no Agora token and no App Certificate — tokens are
 * minted per participant by a callable function and never persisted.
 */
export interface CallDoc {
  callId: string;
  hotelId: string;
  roomId: string;
  roomNumber: string;
  guestId: string;
  department: Department;
  receptionistId: string | null;
  /** Every receptionist notified for this call; used for authorization. */
  candidateReceptionistIds: string[];
  status: CallStatus;
  /** Agora channel, always `call_<uuid>` — never a room number. */
  agoraChannel: string;
  guestUid: number;
  receptionistUid: number;
  /** Reason recorded when the call reaches DECLINED / MISSED / FAILED. */
  endReason?: string | null;
  /**
   * Who answered or declined, as display data captured at the moment they
   * did. Denormalised because the history screen has no other way to put a
   * name on a row: neither role may read every staff document, and a name
   * looked up later would be whatever the row says *now*, not who took the
   * call. Absent on calls that predate this field — render "Receptionist".
   *
   * `responderIsOwner` is a flag rather than a name because the owner's
   * receptionist row carries the hotel's name, and "Seaside Residency
   * answered" is not what a receptionist reading the log needs to know.
   */
  responderName?: string | null;
  responderIsOwner?: boolean;
  createdAt?: unknown;
  updatedAt?: unknown;
  answeredAt?: unknown;
  connectedAt?: unknown;
  endedAt?: unknown;
  /** Ring deadline, evaluated by the scheduled timeout sweeper. */
  ringExpiresAt?: unknown;
  durationSeconds: number;
}

/** UI-only state machine for the guest web app. */
export const GUEST_UI_STATE = {
  IDLE: 'IDLE',
  CALLING: 'CALLING',
  RINGING: 'RINGING',
  CONNECTED: 'CONNECTED',
  RECONNECTING: 'RECONNECTING',
  ENDED: 'ENDED',
  FAILED: 'FAILED',
} as const;

export type GuestUiState = (typeof GUEST_UI_STATE)[keyof typeof GUEST_UI_STATE];

/** UI-only state machine for the receptionist mobile app. */
export const RECEPTIONIST_UI_STATE = {
  AVAILABLE: 'AVAILABLE',
  INCOMING_CALL: 'INCOMING_CALL',
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  RECONNECTING: 'RECONNECTING',
  ENDED: 'ENDED',
} as const;

export type ReceptionistUiState =
  (typeof RECEPTIONIST_UI_STATE)[keyof typeof RECEPTIONIST_UI_STATE];
